"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { CMSData } from "@/cms/models";

export default function DesignPreview({
  cmsData,
  profile,
}: {
  cmsData: CMSData;
  profile: { mode: string; sourceUrl?: string };
}) {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState("");
  useEffect(() => {
    let version = "";
    let stopped = false;
    const timer = setInterval(async () => {
      try {
        const response = await fetch("/api/design/status", {
          cache: "no-store",
        });
        if (!response.ok || stopped) return;
        const data = await response.json();
        if (version && version !== data.version) router.refresh();
        version = data.version;
      } catch {
        /* The dev server may briefly restart during an edit. */
      }
    }, 750);
    const submit = (event: SubmitEvent) => {
      const form = event.target as HTMLFormElement;
      const target = form.action
        ? new URL(form.action, location.href)
        : undefined;
      if (
        target &&
        ["http:", "https:"].includes(target.protocol) &&
        target.origin !== location.origin
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();
        setNotice("Submission simulated locally.");
      }
    };
    const navigate = (event: MouseEvent) => {
      const link = (event.target as Element)?.closest?.("a");
      if (!link || !profile.sourceUrl) return;
      const target = new URL(link.href, location.href);
      if (target.origin === new URL(profile.sourceUrl).origin) {
        event.preventDefault();
        event.stopImmediatePropagation();
        router.push(`${target.pathname}${target.search}${target.hash}`);
      }
    };
    document.addEventListener("submit", submit, true);
    document.addEventListener("click", navigate, true);
    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener("submit", submit, true);
      document.removeEventListener("click", navigate, true);
    };
  }, [router, profile.sourceUrl]);
  return (
    <aside
      style={{
        position: "fixed",
        bottom: 12,
        left: 12,
        zIndex: 2147483647,
        font: "13px system-ui",
        background: "#17231e",
        color: "#fff",
        borderRadius: 8,
        padding: 10,
        maxWidth: 360,
        boxShadow: "0 2px 12px #0003",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{ color: "inherit" }}
      >
        Local design · {profile.mode} · {open ? "Hide" : "Pages"}
      </button>
      {open && (
        <div style={{ marginTop: 8, display: "grid", gap: 8 }}>
          {profile.sourceUrl && (
            <span>{new URL(profile.sourceUrl).hostname}</span>
          )}
          <label>
            Page{" "}
            <select
              aria-label="Preview page"
              defaultValue=""
              onChange={(event) => {
                if (event.target.value) router.push(event.target.value);
              }}
              style={{ color: "#111", maxWidth: 290 }}
            >
              <option value="">Choose a configured page</option>
              {cmsData.pages
                .filter(
                  (page) =>
                    page.type !== "footer" &&
                    page.type !== "article" &&
                    !["casedetails", "/casedetails"].includes(page.slug),
                )
                .map((page, index) => (
                  <option
                    key={index}
                    value={`/${page.slug.replace(/^\//, "")}`}
                  >
                    {page.menuTitle || page.browserTitle || page.slug}
                  </option>
                ))}
            </select>
          </label>
          <span>
            Forms are simulated. Settings edits refresh automatically.
          </span>
          <a
            href="/api/design/settings"
            download="settings.json"
            style={{ color: "inherit", textDecoration: "underline" }}
          >
            Download full settings.json
          </a>
        </div>
      )}
      {notice && <div role="status">{notice}</div>}
    </aside>
  );
}
